import React, { useState } from 'react';
import { referralsService, ReferredPerson } from '../../services/referrals.service';
import './ReferralForm.css';

interface ReferralFormProps {
  referrerId: string;
  onClose: () => void;
  onSuccess: () => void;
  darkMode?: boolean;
}

const ReferralForm: React.FC<ReferralFormProps> = ({ 
  referrerId, 
  onClose, 
  onSuccess,
  darkMode = false 
}) => {
  const [formData, setFormData] = useState({
    candidateName: '',
    candidateEmail: '',
    candidatePhone: '',
    companyId: '',
    companyName: '',
    position: '',
    notes: '',
    referralBonus: 0
  });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    
    // Validation
    if (!formData.candidateName || !formData.candidateEmail || !formData.companyName || !formData.position) {
      setError('Please fill in all required fields');
      return;
    }

    setLoading(true);
    try {
      await referralsService.addReferredPerson(referrerId, {
        candidateName: formData.candidateName,
        candidateEmail: formData.candidateEmail,
        candidatePhone: formData.candidatePhone,
        companyId: formData.companyId || `company_${Date.now()}`,
        companyName: formData.companyName,
        position: formData.position,
        status: 'submitted',
        notes: formData.notes,
        referralBonus: formData.referralBonus
      });
      
      onSuccess();
      onClose();
    } catch (err) {
      setError('Failed to submit referral. Please try again.');
      console.error('Error submitting referral:', err);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className={`referral-form-overlay ${darkMode ? 'dark' : 'light'}`}>
      <div className="referral-form-modal">
        <div className="modal-header">
          <h3>Refer a Candidate</h3>
          <button className="close-btn" onClick={onClose} aria-label="Close">
            ×
          </button>
        </div>

        <form onSubmit={handleSubmit} className="referral-form">
          {error && (
            <div className="error-message">
              {error}
            </div>
          )}

          <div className="form-group">
            <label htmlFor="candidateName">
              Candidate Name <span className="required">*</span>
            </label>
            <input
              type="text"
              id="candidateName"
              value={formData.candidateName}
              onChange={(e) => setFormData(prev => ({ ...prev, candidateName: e.target.value }))}
              placeholder="John Doe"
              required
            />
          </div>

          <div className="form-group">
            <label htmlFor="candidateEmail">
              Candidate Email <span className="required">*</span>
            </label>
            <input
              type="email"
              id="candidateEmail"
              value={formData.candidateEmail}
              onChange={(e) => setFormData(prev => ({ ...prev, candidateEmail: e.target.value }))}
              placeholder="john.doe@example.com"
              required
            />
          </div>

          <div className="form-group">
            <label htmlFor="candidatePhone">
              Phone Number
            </label>
            <input
              type="tel"
              id="candidatePhone"
              value={formData.candidatePhone}
              onChange={(e) => setFormData(prev => ({ ...prev, candidatePhone: e.target.value }))}
              placeholder="+1 (555) 123-4567"
            />
          </div>

          <div className="form-group">
            <label htmlFor="companyName">
              Company Name <span className="required">*</span>
            </label>
            <input
              type="text"
              id="companyName"
              value={formData.companyName}
              onChange={(e) => setFormData(prev => ({ ...prev, companyName: e.target.value }))}
              placeholder="Acme Corporation"
              required
            />
          </div>

          <div className="form-group">
            <label htmlFor="position">
              Position <span className="required">*</span>
            </label>
            <input
              type="text"
              id="position"
              value={formData.position}
              onChange={(e) => setFormData(prev => ({ ...prev, position: e.target.value }))}
              placeholder="Senior Software Engineer"
              required
            />
          </div>

          <div className="form-group">
            <label htmlFor="referralBonus">
              Expected Referral Bonus ($)
            </label>
            <input
              type="number"
              id="referralBonus"
              value={formData.referralBonus}
              onChange={(e) => setFormData(prev => ({ ...prev, referralBonus: Number(e.target.value) }))}
              placeholder="5000"
              min="0"
            />
          </div>

          <div className="form-group">
            <label htmlFor="notes">
              Additional Notes
            </label>
            <textarea
              id="notes"
              value={formData.notes}
              onChange={(e) => setFormData(prev => ({ ...prev, notes: e.target.value }))}
              placeholder="Why do you think this candidate would be a good fit?"
              rows={4}
            />
          </div>

          <div className="form-actions">
            <button type="button" className="cancel-btn" onClick={onClose}>
              Cancel
            </button>
            <button type="submit" className="submit-btn" disabled={loading}>
              {loading ? 'Submitting...' : 'Submit Referral'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default ReferralForm;